import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Stack, Button, TextField, MenuItem, Grid,
  Paper, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, Table, TableBody, TableCell, TableHead,
  TableRow, TableContainer
} from '@mui/material';
import { Add, Delete, CalendarMonth } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import axios from 'axios';

const api = {
  get:    (url, cfg={}) => axios.get(`${process.env.REACT_APP_API_URL}${url}`, { ...cfg, headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug') }}),
  post:   (url, data)   => axios.post(`${process.env.REACT_APP_API_URL}${url}`, data, { headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug') }}),
  delete: (url)         => axios.delete(`${process.env.REACT_APP_API_URL}${url}`, { headers: { Authorization: `Bearer ${localStorage.getItem('sms_token')}`, 'X-School-Slug': localStorage.getItem('sms_slug') }}),
};

const DAYS = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const HOURS = ['07:00','07:30','08:00','08:30','09:00','09:30','10:00','10:30','11:00','11:30',
               '12:00','12:30','13:00','13:30','14:00','14:30','15:00','15:30','16:00','16:30','17:00'];

const COLORS = ['#1976d2','#2e7d32','#c9952a','#7b1fa2','#c62828','#00838f','#4527a0','#558b2f'];

export default function Timetable() {
  const { enqueueSnackbar } = useSnackbar();
  const [courses, setCourses]   = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [years, setYears]       = useState([]);
  const [slots, setSlots]       = useState([]);
  const [selectedYear, setYear] = useState('');
  const [semester, setSemester] = useState(1);
  const [filterProg, setFilterProg] = useState('');
  const [programmes, setProgrammes] = useState([]);
  const [dialog, setDialog]     = useState(false);
  const [saving, setSaving]     = useState(false);
  const [view, setView]         = useState('grid'); // grid | list
  const [form, setForm] = useState({
    course_id:'', teacher_id:'', venue:'', day_of_week:1, start_time:'08:00', end_time:'09:00', semester:1
  });

  useEffect(() => {
    api.get('/tertiary/courses').then(r => setCourses(r.data.data || []));
    api.get('/teachers').then(r => setTeachers(r.data.data || []));
    api.get('/academic-years').then(r => {
      const yrs = r.data.data || [];
      setYears(yrs);
      // Default to first/most recent year
      if (yrs.length > 0) setYear(yrs[0].id);
    });
    api.get('/tertiary/programmes').then(r => setProgrammes(r.data.data || []));
  }, []);

  const load = () => {
    if (!selectedYear) return;
    const params = { academic_year_id: selectedYear, semester };
    if (filterProg) params.programme_id = filterProg;
    api.get('/tertiary/timetable', { params }).then(r => setSlots(r.data.data || []));
  };

  useEffect(load, [selectedYear, semester, filterProg]);

  const setF = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    if (!form.course_id || !form.day_of_week || !form.start_time || !form.end_time)
      return enqueueSnackbar('Course, day and times are required', { variant: 'warning' });
    setSaving(true);
    try {
      await api.post('/tertiary/timetable', { ...form, academic_year_id: selectedYear, semester });
      enqueueSnackbar('Slot added', { variant: 'success' });
      setDialog(false); load();
    } catch (e) { enqueueSnackbar(e.response?.data?.message || 'Failed', { variant: 'error' }); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this slot?')) return;
    try { await api.delete(`/tertiary/timetable/${id}`); load(); }
    catch { enqueueSnackbar('Failed', { variant: 'error' }); }
  };

  // Build grid: day → time → slots
  const courseColor = {};
  courses.forEach((c, i) => { courseColor[c.id] = COLORS[i % COLORS.length]; });

  const gridData = {};
  DAYS.slice(1).forEach(d => { gridData[d] = {}; });
  slots.forEach(s => {
    const day = DAYS[s.day_of_week];
    if (!gridData[day]) gridData[day] = {};
    const key = s.start_time?.slice(0,5);
    if (!gridData[day][key]) gridData[day][key] = [];
    gridData[day][key].push(s);
  });

  const usedTimes = [...new Set(slots.map(s => s.start_time?.slice(0,5)))].sort();

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Timetable</Typography>
          <Typography variant="body2" color="text.secondary">Lecture schedule by day, time and venue</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant={view==='grid'?'contained':'outlined'} size="small" onClick={()=>setView('grid')}>Grid</Button>
          <Button variant={view==='list'?'contained':'outlined'} size="small" onClick={()=>setView('list')}>List</Button>
          <Button variant="contained" startIcon={<Add/>} onClick={()=>setDialog(true)} sx={{ bgcolor:'#0f2b4a' }}>
            Add Slot
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} flexWrap="wrap">
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e=>setYear(e.target.value)} sx={{ minWidth:160 }}>
          <MenuItem value="">Select Year</MenuItem>
          {years.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Semester" value={semester} onChange={e=>setSemester(e.target.value)} sx={{ minWidth:130 }}>
          <MenuItem value={1}>Semester 1</MenuItem>
          <MenuItem value={2}>Semester 2</MenuItem>
        </TextField>
        <TextField select size="small" label="Filter by Programme" value={filterProg} onChange={e=>setFilterProg(e.target.value)} sx={{ minWidth:220 }}>
          <MenuItem value="">All Programmes</MenuItem>
          {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
        </TextField>
      </Stack>

      {slots.length === 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>No timetable slots yet. Click "Add Slot" to schedule lectures.</Alert>
      )}

      {/* GRID VIEW */}
      {view === 'grid' && slots.length > 0 && (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#0f2b4a' }}>
                <TableCell sx={{ color:'white', fontWeight:700, width:80 }}>Time</TableCell>
                {DAYS.slice(1,7).map(d => (
                  <TableCell key={d} sx={{ color:'white', fontWeight:700 }}>{d}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {(usedTimes.length ? usedTimes : HOURS.slice(2,10)).map(time => (
                <TableRow key={time} hover>
                  <TableCell sx={{ fontWeight:600, fontSize:12, bgcolor:'#f8f9fc' }}>{time}</TableCell>
                  {DAYS.slice(1,7).map(day => {
                    const daySlots = gridData[day]?.[time] || [];
                    return (
                      <TableCell key={day} sx={{ p:0.5, verticalAlign:'top', minWidth:120 }}>
                        {daySlots.map(s => (
                          <Box key={s.id} sx={{ bgcolor: courseColor[s.course_id]||'#1976d2', borderRadius:1, p:0.8, mb:0.5, color:'white', position:'relative' }}>
                            <Typography fontSize={11} fontWeight={700}>{s.course_code}</Typography>
                            <Typography fontSize={10} sx={{ opacity:0.9 }}>{s.course_name}</Typography>
                            {s.venue && <Typography fontSize={10} sx={{ opacity:0.8 }}>📍 {s.venue}</Typography>}
                            {s.lecturer_name && <Typography fontSize={10} sx={{ opacity:0.8 }}>👤 {s.lecturer_name}</Typography>}
                            <Typography fontSize={10} sx={{ opacity:0.7 }}>{s.start_time?.slice(0,5)}–{s.end_time?.slice(0,5)}</Typography>
                            <IconButton size="small" onClick={()=>remove(s.id)} sx={{ position:'absolute', top:0, right:0, color:'rgba(255,255,255,0.7)', p:0.2 }}>
                              <Delete sx={{ fontSize:14 }}/>
                            </IconButton>
                          </Box>
                        ))}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {/* LIST VIEW */}
      {view === 'list' && (
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius:2 }}>
          <Table size="small">
            <TableHead><TableRow sx={{ bgcolor:'#f8f9fc' }}>
              {['Day','Time','Course','Lecturer','Venue','Level',''].map(h => (
                <TableCell key={h} sx={{ fontWeight:700, fontSize:12 }}>{h}</TableCell>
              ))}
            </TableRow></TableHead>
            <TableBody>
              {slots.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py:4, color:'text.secondary' }}>No slots scheduled</TableCell></TableRow>
              ) : slots.sort((a,b) => a.day_of_week - b.day_of_week || a.start_time?.localeCompare(b.start_time)).map(s => (
                <TableRow key={s.id} hover>
                  <TableCell><Chip label={DAYS[s.day_of_week]} size="small" /></TableCell>
                  <TableCell><Typography fontSize={13}>{s.start_time?.slice(0,5)} – {s.end_time?.slice(0,5)}</Typography></TableCell>
                  <TableCell>
                    <Typography fontSize={13} fontWeight={600}>{s.course_name}</Typography>
                    <Typography fontSize={11} color="text.secondary">{s.course_code} · Level {s.level}</Typography>
                  </TableCell>
                  <TableCell><Typography fontSize={13}>{s.lecturer_name || '—'}</Typography></TableCell>
                  <TableCell><Typography fontSize={13}>{s.venue || '—'}</Typography></TableCell>
                  <TableCell><Chip label={`Level ${s.level}`} size="small" variant="outlined"/></TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={()=>remove(s.id)}><Delete fontSize="small"/></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Slot Dialog */}
      <Dialog open={dialog} onClose={()=>setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor:'#0f2b4a', color:'white' }}>Add Timetable Slot</DialogTitle>
        <DialogContent sx={{ pt:'20px !important' }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" select label="Course *" value={form.course_id} onChange={setF('course_id')}>
                <MenuItem value="">Select Course</MenuItem>
                {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" select label="Lecturer" value={form.teacher_id} onChange={setF('teacher_id')}>
                <MenuItem value="">None assigned</MenuItem>
                {teachers.map(t => <MenuItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" select label="Day *" value={form.day_of_week} onChange={setF('day_of_week')}>
                {DAYS.slice(1).map((d,i) => <MenuItem key={d} value={i+1}>{d}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Venue / Room" value={form.venue} onChange={setF('venue')} placeholder="e.g. LT-1, Room 204" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" select label="Start Time *" value={form.start_time} onChange={setF('start_time')}>
                {HOURS.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" select label="End Time *" value={form.end_time} onChange={setF('end_time')}>
                {HOURS.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving} sx={{ bgcolor:'#0f2b4a' }}>
            {saving ? 'Saving...' : 'Add Slot'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
